import { InventoryLabelData } from '../types/inventoryLabel';

interface InventoryUnit {
  id: string;
  name: string;
  system_code: string;
  text: string;
  unit_type: 'LIU' | 'BIU' | 'PIU' | 'NIU' | 'AIU';
  content_items?: Array<{
    product_name: string;
    quantity: number;
    product_custom_code?: string;
    sorting_codes?: Array<string | { code: string }>;
  }>;
  // 下面两项只用于识别原箱/原托盘单元并打印其跟踪号、参考号；不传则标签内容不变
  remarks?: string | null;
  sorting_group_name?: string;
}

// 原箱/原托盘入库创建的库存单元所在分拣组的名称前缀（后端 create_original_container_unit 生成）
const ORIGINAL_CONTAINER_GROUP_PREFIXES = ['SYS-原箱入库分拣组', 'SYS-原托盘入库分拣组'];

// 把原箱/原托盘单元的备注转成标签文字：入库时保存的"跟踪号-X；第三方参考号-Y；重量为预估"拆开，号码各占一行，其他文字（旧数据、手工修改）整体作为备注
const formatOriginalContainerRemarks = (remarks?: string | null): string[] => {
  const text = (remarks || '').trim();
  if (!text) return [];
  const lines: string[] = [];
  for (const part of text.split('；').map(item => item.trim())) {
    if (part === '重量为预估') continue; // 只是库存单元的记录，不打印在标签上
    if (part.startsWith('跟踪号-') && part.length > '跟踪号-'.length) lines.push(`跟踪号: ${part.slice('跟踪号-'.length)}`);
    else if (part.startsWith('第三方参考号-') && part.length > '第三方参考号-'.length) lines.push(`参考号: ${part.slice('第三方参考号-'.length)}`);
    else return [`备注: ${text}`];
  }
  return lines;
};

export class InventoryUnitLabelAdapter {
  transform(source: InventoryUnit): InventoryLabelData {
    // 单元类型显示文案（完整文案，供后端/模板直接渲染）
    const unitTypeDisplayMap: Record<'LIU' | 'BIU' | 'PIU' | 'NIU' | 'AIU', string> = {
      LIU: 'Loose Inventory Unit（散货库存单元）',
      BIU: 'Box Inventory Unit（箱库存单元）',
      PIU: 'Pallet Inventory Unit（托盘库存单元）',
      NIU: 'Non-Inventory Unit（非产品库存单元）',
      AIU: 'Abnormal Inventory Unit（异常库存单元）'
    };

    // 根据单元类型确定字体大小
    const getFontSize = (type: 'LIU' | 'BIU' | 'PIU' | 'NIU' | 'AIU') => {
      switch (type) {
        case 'LIU': return 10;  // 散货单元
        case 'BIU': return 12;  // 箱单元
        case 'PIU': return 14;  // 托盘单元
        case 'NIU': return 12;  // 非产品库存单元，沿用箱字号
        case 'AIU': return 10;  // 异常单元，沿用散货字号
        default: return 10;
      }
    };

    // 取分拣条码列表中的第一个，仅显示一个；兼容两种数据源（纯字符串数组 / {code,...}对象数组）；没有则返回undefined
    const getFirstSortingCode = (sortingCodes?: Array<string | { code: string }>): string | undefined => {
      if (!Array.isArray(sortingCodes) || sortingCodes.length === 0) return undefined;
      const first = sortingCodes[0];
      const code = typeof first === 'string' ? first : first?.code;
      return code || undefined;
    };

    // 根据单元类型格式化名称：LIU只有一个产品项，附带其分拣条码（没有分拣条码则只显示名称）
    const formatName = (name: string, type: 'LIU' | 'BIU' | 'PIU' | 'NIU' | 'AIU') => {
      switch (type) {
        case 'LIU': {
          const code = getFirstSortingCode(source.content_items?.[0]?.sorting_codes);
          return code ? `${name}(${code})` : name;
        }
        case 'BIU': return `${name}（箱）`;  // 箱单元
        case 'PIU': return `${name}（托盘）`;  // 托盘单元
        case 'NIU': return name; // 非产品库存单元，不附加后缀
        case 'AIU': return name; // 异常库存单元，不附加后缀
        default: return name;
      }
    };

    // 格式化内容物信息（仅BIU使用）："产品名称(分拣条码)×数量"，没有分拣条码则不显示括号
    const formatContentItems = (
      items?: Array<{product_name: string; quantity: number; product_custom_code?: string; sorting_codes?: Array<string | { code: string }>}>
    ) => {
      if (!items || items.length === 0) {
        return '无内容物信息';
      }
      return items.map(item => {
        if (source.unit_type !== 'BIU') {
          return `${item.product_name} × ${item.quantity}`;
        }
        const code = getFirstSortingCode(item.sorting_codes);
        return code ? `${item.product_name}(${code})×${item.quantity}` : `${item.product_name}×${item.quantity}`;
      }).join('\n');
    };

    // 原箱/原托盘单元没有内容物：重打标签时显示和入库时一样的文字，代替"无内容物信息"；其他单元不受影响
    const formatLabelContent = () => {
      const isOriginalContainer = (source.unit_type === 'BIU' || source.unit_type === 'PIU')
        && ORIGINAL_CONTAINER_GROUP_PREFIXES.some(prefix => (source.sorting_group_name || '').startsWith(prefix))
        && !(source.content_items && source.content_items.length > 0);
      if (!isOriginalContainer) return formatContentItems(source.content_items);
      return ['（原箱/原托盘入库）', ...formatOriginalContainerRemarks(source.remarks)].join('\n');
    };

    // 根据单元类型选择模板
    // - LIU: 使用默认小尺寸模板（2.25×1.25 英寸）
    // - BIU: 使用箱模板（4×6 英寸）
    // - PIU: 使用托盘模板（4×6 英寸）
    // - NIU: 与 BIU 一样使用箱模板（4×6 英寸）
    let templateId: 'box-unit-label' | 'pallet-unit-label' | 'default-unit-label' = 'box-unit-label';
    if (source.unit_type === 'PIU') templateId = 'pallet-unit-label';
    if (source.unit_type === 'LIU' || source.unit_type === 'AIU') templateId = 'default-unit-label';

    const result: InventoryLabelData = {
      id: source.id,
      templateId,
      values: {
        name: formatName(source.name, source.unit_type),
        systemCode: source.system_code,
        forecastCode: source.system_code,
        quantity: 1,
        text: source.system_code,
        unit_type: (['LIU','BIU','PIU','NIU','AIU'] as const).includes(source.unit_type as any) ? source.unit_type as any : 'LIU',
        unit_type_display: unitTypeDisplayMap[source.unit_type] || 'Unknown Unit Type',
        fontSize: getFontSize(source.unit_type),
        content_items: formatLabelContent()
      }
    };
    return result;
  }

  validate(data: InventoryLabelData): boolean {
    const required = ['name', 'systemCode', 'forecastCode', 'quantity', 'text', 'unit_type', 'unit_type_display', 'fontSize'];
    if (data.templateId === 'box-unit-label') {
      required.push('content_items');
    }
    return required.every(field => 
      data.values && typeof data.values[field] !== 'undefined'
    );
  }
} 